import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDeploymentGroup, useDeletePipeline } from '../../services/queries';
import { LoadingSpinner, EmptyState, ConfirmDialog, ErrorAlert } from '../common';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import { PipelineSummaryDto } from '../../types/pipelineFlow';

export function DeploymentGroupDetails() {
  const { id } = useParams<{ id: string }>();
  const { data: group, isLoading: groupLoading, error: groupError } = useDeploymentGroup(id!);

  if (groupLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (groupError) {
    return <ErrorAlert error={groupError instanceof Error ? groupError.message : 'An error occurred'} />;
  }

  if (!group) {
    return <ErrorAlert error="Deployment group not found" />;
  }

  // Get pipelines from the group response
  const pipelines = group.pipelines || [];

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <nav className="flex mb-4" aria-label="Breadcrumb">
            <ol className="inline-flex items-center space-x-1 md:space-x-3">
              <li>
                <Link to="/admin/deployment-groups" className="text-gray-500 hover:text-gray-700">
                  Deployment Groups
                </Link>
              </li>
              <li>
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="ml-1 text-gray-500 md:ml-2">{group.name}</span>
                </div>
              </li>
            </ol>
          </nav>
          
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
            {group.name}
          </h2>
          <div className="mt-1 flex flex-col sm:mt-0 sm:flex-row sm:flex-wrap sm:space-x-6">
            <div className="mt-2 flex items-center text-sm text-gray-500">
              <svg className="mr-1.5 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5a2.25 2.25 0 002.25-2.25m-18 0v-7.5A2.25 2.25 0 005.25 9h13.5a2.25 2.25 0 002.25 2.25v7.5" />
              </svg>
              Created {new Date(group.createdOn).toLocaleDateString()}
            </div>
            <div className="mt-2 flex items-center text-sm text-gray-500">
              <svg className="mr-1.5 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
              </svg>
              Last modified {new Date(group.lastModifiedOn).toLocaleDateString()}
            </div>
          </div>
        </div>
        <div className="mt-4 flex md:ml-4 md:mt-0">
          <Link
            to={`/admin/deployment-groups/${group.id}/edit`}
            className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            Edit Group
          </Link>
          <Link
            to={`/admin/deployment-groups/${group.id}/select-pipelines`}
            className="ml-3 inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
          >
            Add Pipelines
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
          <dt className="truncate text-sm font-medium text-gray-500">Total Pipelines</dt>
          <dd className="mt-1 text-3xl font-semibold tracking-tight text-gray-900">{pipelines.length}</dd>
        </div>
        <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
          <dt className="truncate text-sm font-medium text-gray-500">Active Pipelines</dt>
          <dd className="mt-1 text-3xl font-semibold tracking-tight text-gray-900">
            {pipelines.filter((d: PipelineSummaryDto) => !d.isDeleted).length}
          </dd>
        </div>
        <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
          <dt className="truncate text-sm font-medium text-gray-500">Deleted Pipelines</dt>
          <dd className="mt-1 text-3xl font-semibold tracking-tight text-gray-900">
            {pipelines.filter((d: PipelineSummaryDto) => d.isDeleted).length}
          </dd>
        </div>
      </div>

      {/* Pipelines */}
      <div className="mt-8">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h3 className="text-lg font-semibold leading-6 text-gray-900">Pipelines</h3>
            <p className="mt-2 text-sm text-gray-700">
              Pipelines in this deployment group.
            </p>
          </div>
        </div>

        <div className="mt-6">
          {pipelines.length === 0 ? (
            <EmptyState
              title="No pipelines found"
              description="Create your first pipelines to get started with pipeline tracking."
              action={{
                label: "Add Pipelines",
                onClick: () => window.location.href = `/admin/deployment-groups/${group.id}/select-pipelines`
              }}
              icon={
                <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              }
            />
          ) : (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {pipelines.map((pipeline: PipelineSummaryDto) => (
                <PipelineCard key={pipeline.id} pipeline={pipeline} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface PipelineCardProps {
  pipeline: PipelineSummaryDto;
}

function PipelineCard({ pipeline }: PipelineCardProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const deletePipeline = useDeletePipeline();
  const errorHandler = useErrorHandler();

  const handleDelete = async () => {
    try {
      await deletePipeline.mutateAsync(pipeline.id);
      setShowDeleteConfirm(false);
    } catch (error) {
      errorHandler.showError(error instanceof Error ? error : new Error('Failed to delete pipeline'));
    }
  };

  return (
    <>
      <div className="relative rounded-lg border border-gray-200 bg-white p-6 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <Link
              to={`/pipelines/${pipeline.id}`}
              className="text-lg font-semibold text-gray-900 hover:text-blue-600"
            >
              {pipeline.name}
            </Link>
            <p className="mt-1 text-sm text-gray-500">{pipeline.pipelineName}</p>
          </div>
          <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-800">
            ID: {pipeline.definitionId}
          </span>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-4 text-sm text-gray-500">
          <div>
            <span className="font-medium">Pipeline:</span> {pipeline.pipelineName}
          </div>
          <div>
            <span className="font-medium">Definition ID:</span> {pipeline.definitionId}
          </div>
          <div className="col-span-2">
            <span className="font-medium">Last Modified:</span>{' '}
            {new Date(pipeline.lastModifiedOn).toLocaleDateString()}
          </div>
        </div>

        <div className="mt-4 flex justify-end items-center space-x-3">
          <Link
            to={`/pipelines/${pipeline.id}`}
            className="text-sm font-medium text-blue-600 hover:text-blue-500"
          >
            View Details
          </Link>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="btn-ghost-danger"
            disabled={deletePipeline.isPending}
          >
            {deletePipeline.isPending ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Pipeline"
        message={`Are you sure you want to delete "${pipeline.name}"? This action cannot be undone and will also delete all pipeline run history.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isDestructive={true}
      />

      {errorHandler.isVisible && errorHandler.error && (
        <ErrorAlert 
          error={errorHandler.error.message} 
          onDismiss={errorHandler.hideError}
        />
      )}
    </>
  );
}